/**
 * ActionItems Component
 *
 * Displays auto-captured action items from the session, separated by speaker.
 * Action item detection logic is in @/services/action-detector.ts
 */

import type { ActionItem } from '@/types';

interface Props {
  items: ActionItem[];
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
}

/**
 * Format timestamp as MM:SS
 */
function formatTimestamp(seconds: number): string {
  const min = Math.floor(seconds / 60);
  const sec = Math.floor(seconds % 60);
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

/**
 * Single action item row with checkbox and delete button
 */
function ActionItemRow({
  item,
  onToggle,
  onDelete
}: {
  item: ActionItem;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="flex items-start gap-2 text-sm py-1 group">
      <button
        onClick={() => onToggle(item.id)}
        className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded border transition-colors ${
          item.completed
            ? 'bg-green-500 border-green-500 text-white'
            : 'border-gray-300 hover:border-green-500'
        }`}
      >
        {item.completed && (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <span className={`${item.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
          {item.text}
        </span>
        <span className="text-xs text-gray-400 ml-2">
          ({formatTimestamp(item.timestamp)})
        </span>
        {item.confidence === 'medium' && (
          <span className="ml-1 text-xs text-yellow-600" title="Auto-detected">
            ?
          </span>
        )}
      </div>
      <button
        onClick={() => onDelete(item.id)}
        className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-opacity flex-shrink-0"
        title="Remove"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Action items list, separated into user and prospect sections
 */
export function ActionItems({ items, onToggle, onDelete }: Props) {
  const userItems = items.filter(i => i.speaker === 'user');
  const prospectItems = items.filter(i => i.speaker === 'prospect');

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
        <span>Action Items</span>
        {items.length > 0 && (
          <span className="text-gray-400 font-normal">({items.length})</span>
        )}
      </h4>

      {userItems.length > 0 && (
        <div>
          <p className="text-xs text-gray-400 mb-1">Your commitments</p>
          {userItems.map(item => (
            <ActionItemRow
              key={item.id}
              item={item}
              onToggle={onToggle}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {prospectItems.length > 0 && (
        <div className={userItems.length > 0 ? 'mt-3' : ''}>
          <p className="text-xs text-gray-400 mb-1">Their commitments</p>
          {prospectItems.map(item => (
            <ActionItemRow
              key={item.id}
              item={item}
              onToggle={onToggle}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {items.length === 0 && (
        <p className="text-xs text-gray-400 italic">
          No action items captured yet
        </p>
      )}
    </div>
  );
}
